import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ImageUploadComponent } from '../image-upload/image-upload.component';
import { ApiService } from '../../../../services/api.service';

/**
 * Modal dialog component for creating or editing Google Ads entries, including title management, image selection, validation (exact dimensions: 1200 x 628), and upload.
 * @component
 * @selector app-google-ads-modal
 * @example
 *   <app-google-ads-modal [isOpen]="true" [data]="adData" (close)="handleClose()" (save)="handleSave($event)"></app-google-ads-modal>
 *
 * Purpose:
 *   Provides a reusable modal UI to create or edit a Google Ad's title and image. Handles image dimension validation, previewing, and uploading to the backend before emitting saved ad data.
 *
 * Lifecycle Hooks:
 *   - ngOnInit(): Initializes modal state by calling initializeModalData().
 *   - ngOnChanges(changes: SimpleChanges): Re-initializes modal when the `data` input changes and logs incoming data.
 *
 * Key methods:
 *   - onClose(): Emits the `close` event to request closing the modal.
 *   - onSave(): Validates title and image presence/constraints, constructs ad payload and emits the `save` event with { title, image, imagePreview, imageUrl } if valid.
 *   - onImageSelected(file: File): Clears image errors and starts dimension validation for selected file.
 *   - onImageRemoved(): Clears selected image, previews, and URL; removes related validation errors.
 *   - initializeModalData(): Populates local state (adTitle, imagePreview, imageUrl) from input `data`.
 *   - validateImageDimensions(file: File): Reads the file, ensures dimensions are exactly 1200 x 628 px, sets preview/selection, or records validation errors.
 *   - uploadImage(file: File): Uploads the selected image via ApiService.uploadCampaignFile, manages isUploadingImage flag, and sets imageUrl on success.
 *
 * State & validation:
 *   - adTitle: string - local title bound to the form.
 *   - selectedImage: File | null - local selected image file pending upload.
 *   - imagePreview: string | null - data URL used for previewing selected images.
 *   - imageUrl: string | null - server URL returned after successful upload.
 *   - isUploadingImage: boolean - true while an upload is in progress.
 *   - validationErrors: { [key: string]: string } - per-field validation messages.
 *   - isFormValid: boolean - overall form validity flag used to prevent invalid saves.
 *
 * @input {boolean} isOpen - Controls whether the modal is visible.
 * @input {any} data - Initial ad payload (optional fields: title, imagePreview, imageUrl) used to prefill the modal for editing.
 * @output {EventEmitter<void>} close - Emitted when the user closes the modal without saving.
 * @output {EventEmitter<any>} save - Emitted with the ad payload when the form is successfully validated and saved: { title, image, imagePreview, imageUrl }.
 */
@Component({
  selector: 'app-google-ads-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ImageUploadComponent],
  templateUrl: './google-ads-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './google-ads-modal.component.scss',
})
export class GoogleAdsModalComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  @Input() data: any = {};

  adTitle = '';
  selectedImage: File | null = null;
  imagePreview: string | null = null;
  imageUrl: string | null = null;
  isUploadingImage = false;

  // Validation properties
  validationErrors: Record<string, string> = {};
  isFormValid = true;

  constructor(private apiService: ApiService) {}

  onClose(): void {
    this.close.emit();
  }
  ngOnInit(): void {
    this.initializeModalData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if data input has changed
    if (changes['data'] && changes['data'].currentValue) {
      console.log('Modal data changed:', changes['data'].currentValue);
      this.initializeModalData();
    }
  }

  /**
   * Initialize modal fields (adTitle, imagePreview, imageUrl) from the component's data property or reset them to defaults when no data is provided.
   * @example
   * this.initializeModalData()
   * undefined
   * @returns {{void}} Void - does not return a value.
   **/
  private initializeModalData(): void {
    console.log('Initializing modal data with:', this.data);

    if (this.data) {
      this.adTitle = this.data.title || '';
      this.imagePreview = this.data.imagePreview || null;
      this.imageUrl = this.data.imageUrl || null;
      console.log('Modal data initialized:', {
        adTitle: this.adTitle,
        imagePreview: this.imagePreview,
        imageUrl: this.imageUrl,
      });
    } else {
      console.log('No data provided to modal');
      this.adTitle = '';
      this.imagePreview = null;
      this.imageUrl = null;
    }
  }

  /**
   * Validate form fields (title and image), update validationErrors/isFormValid, and emit ad data when valid.
   * @example
   * nSave()
   * // Emits: { title: string, image: File | null, imagePreview: string | null, imageUrl: string | null } and returns undefined
   * @returns {void} Emits validated ad data via this.save EventEmitter and returns nothing.
   */
  onSave(): void {
    // Clear previous validation errors
    this.validationErrors = {};
    this.isFormValid = true;

    // Validate required fields
    if (!this.adTitle || this.adTitle.trim() === '') {
      this.validationErrors['title'] = 'Title is required';
      this.isFormValid = false;
    } else if (this.adTitle.length > 100) {
      this.validationErrors['title'] = 'Title cannot exceed 100 characters';
      this.isFormValid = false;
    }

    if (!this.selectedImage && !this.imageUrl && !this.imagePreview) {
      this.validationErrors['image'] = 'Image is required (dimensions: 1200 x 628 pixels)';
      this.isFormValid = false;
    }

    // If form is invalid, don't proceed
    if (!this.isFormValid) {
      return;
    }

    const savedImageUrl = this.imageUrl || '';
    const adData = {
      title: this.adTitle,
      image: savedImageUrl,
      imageUrl: savedImageUrl,
    };
    this.releaseLocalImagePreview();
    this.save.emit(adData);
  }

  getPreviewImageUrl(): string {
    return this.imagePreview || this.imageUrl || '';
  }

  onImageSelected(file: File): void {
    // Clear previous validation errors
    this.validationErrors = {};

    // Validate image dimensions
    this.validateImageDimensions(file);
  }

  /**
   * Validates that the provided image file has exactly 1200 x 628 pixels, updates component state and uploads the image when valid.
   * @example
   * validateImageDimensions(sampleFile)
   * undefined
   * @param {{File}} {{file}} - The image file to validate; if valid it will be selected, previewed and uploaded.
   * @returns {{void}} No return value; updates validationErrors, selectedImage and imagePreview on the component and may call uploadImage().
   **/
  private validateImageDimensions(file: File): void {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      const imageUrl = e.target?.result as string;
      img.onload = () => {
        const requiredWidth = 1200;
        const requiredHeight = 628;

        if (img.width !== requiredWidth || img.height !== requiredHeight) {
          this.validationErrors['image'] =
            `Image dimensions must be exactly ${requiredWidth} x ${requiredHeight} pixels. Current dimensions: ${img.width} x ${img.height} pixels.`;
          this.selectedImage = null;
          this.imagePreview = null;
          return;
        }

        // Dimensions are valid, proceed with image selection
        this.imageUrl = null;
        this.selectedImage = file;
        this.imagePreview = imageUrl;

        // Upload to API
        this.uploadImage(file);
      };

      img.onerror = () => {
        this.validationErrors['image'] = 'Failed to load image. Please try again.';
        this.selectedImage = null;
        this.imagePreview = null;
      };

      img.src = imageUrl;
    };

    reader.onerror = () => {
      this.validationErrors['image'] = 'Failed to read image file. Please try again.';
      this.selectedImage = null;
      this.imagePreview = null;
    };

    reader.readAsDataURL(file);
  }

  onImageRemoved(): void {
    this.releaseLocalImagePreview();
    this.imageUrl = null;
    if (this.validationErrors['image']) {
      delete this.validationErrors['image'];
    }
  }

  /** After save, preview uses the API image URL instead of the local data URL. */
  private releaseLocalImagePreview(): void {
    this.selectedImage = null;
    this.imagePreview = null;
  }

  /**
   * Uploads an image file to the campaign API and updates component state based on the response.
   * @example
   * uploadImage(sampleFile)
   * void
   * @param {File} file - File object to upload.
   * @returns {void} Does not return a value; updates component state and logs success or error.
   **/
  private uploadImage(file: File): void {
    this.isUploadingImage = true;
    const formData = new FormData();
    formData.append('g_ad_img', file);

    this.apiService.uploadCampaignFile(formData).subscribe({
      next: response => {
        this.isUploadingImage = false;
        if (response.status && response.data && response.data.g_ad_img) {
          this.imageUrl = response.data.g_ad_img;
          console.log('Google Ad image uploaded successfully:', this.imageUrl);
        } else {
          console.error('Google Ad image upload failed: Invalid response structure');
          this.validationErrors['image'] = 'Image upload failed. Please try again.';
          this.selectedImage = null;
          this.imagePreview = null;
        }
      },
      error: error => {
        this.isUploadingImage = false;
        console.error('Error uploading Google Ad image:', error);
        this.validationErrors['image'] = 'Failed to upload image. Please try again.';
        this.selectedImage = null;
        this.imagePreview = null;
      },
    });
  }
}
